import React from 'react';
import { I18n, Translate } from 'react-redux-i18n';
import { RouteComponentProps, withRouter } from 'react-router-dom';

import chevronDroite from '../../../../img/icones/chevron_droite.svg';
import { PAIR_DEVICE_ONE } from '../../../../routes';
import compteApi from '../../../../network/api/compteApi';
import securite from '../../../../img/home/securite.svg';
import intrus from '../../../../img/home/intrus.svg';
import associerAppareil from '../../../../img/icones/associer_appareil.svg';
import { PairedDevicesDTO } from '../../../../network/api/types/compte/PairedDevicesDTO';
import { InWeboDevice } from '../../../../network/api/types/compte/InWeboDevice';
import { notifyErrorWithMessage, notifySuccess } from '../../../common/Notification';
import { isMobile } from '../../../../services/deviceService';
import SwitchInput from '../../../common/inputs/SwitchInput';
import ConfirmationModal from '../../../common/modals/ConfirmationModal';

type Props = RouteComponentProps;

type State = {
  myPairedDevices: PairedDevicesDTO,
  hardAuth: boolean,
  isModalOpen: boolean,
};

class ReinforceSecurityBox extends React.Component<Props, State> {
  state: State = {
    myPairedDevices: null,
    hardAuth: false,
    isModalOpen: false,
  };

  componentDidMount(): void {
    compteApi.fetchMyPairedDevices()
      .then((myPairedDevices) => {
        this.setState({
          myPairedDevices: {
            devices: myPairedDevices.devices,
          },
        }, () => {
          const oldDevices = localStorage.getItem('numberOfDevices');
          if (oldDevices && oldDevices < this.state.myPairedDevices.devices.length.toString(10)) {
            notifySuccess(I18n.t('PAIR_DEVICE.SUCCESSFULLY_ADDED'));
          }
          localStorage.setItem('numberOfDevices', this.state.myPairedDevices.devices.length.toString(10));
        });
      });
    compteApi.checkMyReinforcedAuth()
      .then((hardauth) => this.setState({ hardAuth: hardauth }));
  }

  reloadPairedDevices = ():void => {
    compteApi.fetchMyPairedDevices()
      .then((myPairedDevices) => {
        this.setState({
          myPairedDevices: {
            devices: myPairedDevices.devices,
          },
        }, () => {
          localStorage.setItem('numberOfDevices', this.state.myPairedDevices.devices.length.toString(10));
        });
      });
  };

  openConfirmationModal = () => {
    this.setState((prevState) => ({ isModalOpen: !prevState.isModalOpen }));
  };

  handleAuthSwitch = () => {
    if (this.state.hardAuth) {
      this.openConfirmationModal();
    } else {
      compteApi.setMyReinforcedAuth(!this.state.hardAuth)
        .then((success) => this.setState((prevState) =>
          ({ hardAuth: success && !prevState.hardAuth }), () => {
          if (success) {
            notifySuccess(I18n.t('message.SUCCESS'));
          } else {
            notifyErrorWithMessage(I18n.t('message.ERROR_MODIF'));
          }
        }));
    }
  };

  handleConfirmedSwitch = () => {
    compteApi.setMyReinforcedAuth(!this.state.hardAuth)
      .then((success) => this.setState((prevState) =>
        ({ hardAuth: success && !prevState.hardAuth })));
  };

  handleDissociate = (device: InWeboDevice): void => {
    compteApi.dissociateDevice(device)
      .then(() => {
        notifySuccess(I18n.t('HOME.SUCCESS_DISOCIATE'));
        this.reloadPairedDevices();
      })
      .catch(() => notifyErrorWithMessage(I18n.t('message.ERROR')));
  };

  isLineHidden = (): string => (this.state.myPairedDevices?.devices?.length && !isMobile ? '' : 'line-hidden');

  isSubHidden = (): string => (this.state.myPairedDevices?.devices?.length && !isMobile ? 'line-hidden' : '');

  secureIsRow = (): string => (this.state.myPairedDevices?.devices?.length && !isMobile ? 'row' : 'column');

  secureIsColumn = (): string => (this.state.myPairedDevices?.devices?.length && !isMobile ? 'column' : 'row');

  render(): React.ReactElement {
    return (
      <div className="account-box security-box">
        <ConfirmationModal
          isOpen={this.state.isModalOpen}
          onClose={this.openConfirmationModal}
          onConfirm={() => {
            this.handleConfirmedSwitch();
            this.openConfirmationModal();
          }}
          titleI18nKey="HOME.HARD_AUTH_CONFIRMATION_TITLE"
          subtTitleI18nKey="HOME.HARD_AUTH_CONFIRMATION"
          confirmeDisabled={false}
        />
        <div className="security-box-padding">
          <div className="box-line column no-border">
            <h4>
              <div className="title-background primary" />
              <Translate value="HOME.REINFORCE_SECURITY" />
            </h4>
            <div className="subtitle">
              <Translate value="HOME.REINFORCE_SECURITY_SUBTITLE" />
            </div>
          </div>
          <div className={`box-line security-intro ${this.secureIsRow()}`}>
            <div className={`security-intro-item ${this.secureIsColumn()}`}>
              <img
                className="security-intro-icon"
                src={securite}
                alt="securite"
              />
              <div className="security-intro-text">
                <div className="intro-item-title">
                  <div className="title-background secondary" />
                  <Translate value="HOME.ADD_SECURITY_LAYER" />
                </div>
                <div className={`intro-item-subtitle ${this.isSubHidden()}`}>
                  <Translate value="HOME.ADD_SECURITY_LAYER_SUBTITLE" />
                </div>
              </div>
            </div>
            <div className={`security-intro-item ${this.secureIsColumn()}`}>
              <img
                className="security-intro-icon"
                src={intrus}
                alt="intrus"
              />
              <div className="security-intro-text">
                <div className="intro-item-title">
                  <div className="title-background secondary" />
                  <Translate value="HOME.PROTECT_ACCOUNT" />
                </div>
                <div className={`intro-item-subtitle ${this.isSubHidden()}`}>
                  <Translate value="HOME.PROTECT_ACCOUNT_SUBTITLE" />
                </div>
              </div>
            </div>
          </div>
          <div className={`box-line column devices-list ${this.isLineHidden()}`}>
            <h5 className="button-title">
              <Translate value="HOME.YOUR_DEVICES" />
            </h5>
            {this.state.myPairedDevices?.devices.map((device) => (
              <div key={device.id} className="info-row">
                <span className="device-title">{device.name}</span>
                <button
                  className="dissociate-button"
                  type="button"
                  onClick={() => this.handleDissociate(device)}
                >
                  <Translate value="HOME.DISSOCIATE" />
                  <img
                    className="chevron"
                    src={chevronDroite}
                    alt="chevron"
                  />
                </button>
              </div>
            ))}
          </div>
          <div className={`box-line column ${this.isLineHidden()} hardauth-box-line`}>
            <h5 className="button-title">
              <Translate value="HOME.HARD_AUTH" />
            </h5>
            <div className="info-row">
              <Translate value="HOME.HARD_AUTH_SUBTITLE" />
              <SwitchInput
                name="hardauth-switch"
                checked={this.state.hardAuth}
                onChange={this.handleAuthSwitch}
                offText={I18n.t('message.NO')}
                onText={I18n.t('message.YES')}
              />
            </div>
          </div>
          <div className="box-line no-border">
            <button
              type="button"
              className="info-row"
              onClick={() => this.props.history.push(PAIR_DEVICE_ONE)}
            >
              <div className="chevron-helper with-icon">
                <img
                  className="pair-icon"
                  src={associerAppareil}
                  alt="associerApareil"
                />
                <Translate value="HOME.PAIR_DEVICE" />
              </div>
              <img
                className="chevron"
                src={chevronDroite}
                alt="chevron"
              />
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default withRouter(ReinforceSecurityBox);
